import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Tooltip,
  Divider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  ListItemText,
  OutlinedInput,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { accountsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { DASHBOARD_COLUMNS, REPORTS_SUB_TABS, MASTER_PERF_SUB_TABS } from '../constants/viewerPermissions';

// Default form-shape for the viewer permission section. Each switch gates a
// matching field on the API payload — see buildPermissionsPayload.
//
// `overrides` is a per-account map of customizations applied on top of the
// global dashboard.columns / reports.sub_tabs rules. Each account entry can
// independently restrict columns and/or reports.
const emptyPermissionsForm = () => ({
  restrictColumns: false,
  columns: [],
  viewSetup: true,
  customSetupAccounts: false,
  setupAccounts: [],
  restrictReports: false,
  reports: [],
  restrictMasterPerf: false,
  masterPerf: [],
  overrides: {},  // { [accountId]: { restrictColumns, columns, restrictReports, reports, restrictMasterPerf, masterPerf } }
});

const emptyOverride = () => ({
  restrictColumns: false,
  columns: [],
  restrictReports: false,
  reports: [],
  restrictMasterPerf: false,
  masterPerf: [],
});

// Hydrate the form from the API permissions object (or null = no restriction).
function permissionsToForm(p) {
  const dash = (p && p.dashboard) || {};
  const rep  = (p && p.reports)   || {};
  const mp   = (p && p.master_performance) || {};
  const dashOv = (dash.account_overrides && typeof dash.account_overrides === 'object') ? dash.account_overrides : {};
  const repOv  = (rep.account_overrides  && typeof rep.account_overrides  === 'object') ? rep.account_overrides  : {};
  const mpOv   = (mp.account_overrides   && typeof mp.account_overrides   === 'object') ? mp.account_overrides   : {};

  // Merge per-account overrides from dashboard + reports + master_performance into one map.
  const allKeys = new Set([...Object.keys(dashOv), ...Object.keys(repOv), ...Object.keys(mpOv)]);
  const overrides = {};
  for (const k of allKeys) {
    const dCols = dashOv[k] && Array.isArray(dashOv[k].columns) ? dashOv[k].columns : null;
    const rSubs = repOv[k]  && Array.isArray(repOv[k].sub_tabs) ? repOv[k].sub_tabs  : null;
    const mSubs = mpOv[k]   && Array.isArray(mpOv[k].sub_tabs)  ? mpOv[k].sub_tabs   : null;
    overrides[String(k)] = {
      restrictColumns: dCols !== null,
      columns: dCols || [],
      restrictReports: rSubs !== null,
      reports: rSubs || [],
      restrictMasterPerf: mSubs !== null,
      masterPerf: mSubs || [],
    };
  }

  return {
    restrictColumns: Array.isArray(dash.columns),
    columns: Array.isArray(dash.columns) ? dash.columns : [],
    viewSetup: dash.view_setup !== false,
    customSetupAccounts: Array.isArray(dash.setup_accounts),
    setupAccounts: Array.isArray(dash.setup_accounts) ? dash.setup_accounts : [],
    restrictReports: Array.isArray(rep.sub_tabs),
    reports: Array.isArray(rep.sub_tabs) ? rep.sub_tabs : [],
    restrictMasterPerf: Array.isArray(mp.sub_tabs),
    masterPerf: Array.isArray(mp.sub_tabs) ? mp.sub_tabs : [],
    overrides,
  };
}

// Build the API payload from form state. Returns null when nothing meaningful
// is set so the server stores NULL = no restriction.
function buildPermissionsPayload(form) {
  const out = {};
  const dash = {};
  if (form.restrictColumns) dash.columns = form.columns;
  if (!form.viewSetup) dash.view_setup = false;
  if (form.customSetupAccounts) dash.setup_accounts = form.setupAccounts;

  // Distribute per-account overrides into dashboard.account_overrides,
  // reports.account_overrides and master_performance.account_overrides. Each
  // entry only contributes if its switch is on.
  const dashOv = {};
  const repOv  = {};
  const mpOv   = {};
  for (const [accId, ov] of Object.entries(form.overrides || {})) {
    if (ov.restrictColumns)    dashOv[accId] = { columns: ov.columns };
    if (ov.restrictReports)    repOv[accId]  = { sub_tabs: ov.reports };
    if (ov.restrictMasterPerf) mpOv[accId]   = { sub_tabs: ov.masterPerf };
  }
  if (Object.keys(dashOv).length) dash.account_overrides = dashOv;

  if (Object.keys(dash).length) out.dashboard = dash;

  const rep = {};
  if (form.restrictReports) rep.sub_tabs = form.reports;
  if (Object.keys(repOv).length) rep.account_overrides = repOv;
  if (Object.keys(rep).length) out.reports = rep;

  const mp = {};
  if (form.restrictMasterPerf) mp.sub_tabs = form.masterPerf;
  if (Object.keys(mpOv).length) mp.account_overrides = mpOv;
  if (Object.keys(mp).length) out.master_performance = mp;

  return Object.keys(out).length ? out : null;
}

const ROLE_LABELS = {
  'admin-root': { label: 'Admin Root', color: 'error' },
  'admin-sub': { label: 'Admin Sub', color: 'warning' },
  'broker': { label: 'Broker', color: 'primary' },
  'client': { label: 'Client', color: 'default' },
  'viewer': { label: 'Viewer', color: 'info' },
};

export default function AccountManagement() {
  const { isAdmin, user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  // Account state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({ mt5_account_id: '', name: '', password: '', group_name: '', profit_share: '' });
  const [editFormData, setEditFormData] = useState({ mt5_account_id: '', name: '', password: '', group_name: '', profit_share: '', hidden_locations: [] });
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');
  const [groupFilter, setGroupFilter] = useState('');

  // User state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({ username: '', password: '', role: 'viewer', allowed_accounts: [], allowed_tabs: [], permissions: emptyPermissionsForm() });
  const [userError, setUserError] = useState('');
  const [userEditDialogOpen, setUserEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userEditFormData, setUserEditFormData] = useState({ password: '', allowed_accounts: [], allowed_tabs: [], permissions: emptyPermissionsForm() });
  const [userEditError, setUserEditError] = useState('');

  const { data: accounts = [] } = useQuery('accounts', () => accountsAPI.getAll().then(res => res.data));
  const { data: users = [] } = useQuery('users', () => usersAPI.getAll().then(res => res.data));

  const createMutation = useMutation(accountsAPI.create, {
    onSuccess: () => { queryClient.invalidateQueries('accounts'); setDialogOpen(false); resetForm(); },
    onError: (err) => setError(err.response?.data?.error || 'Tạo tài khoản thất bại'),
  });

  const updateMutation = useMutation(
    ({ id, data }) => accountsAPI.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries('accounts'); setEditDialogOpen(false); setEditingAccount(null); setEditError(''); },
      onError: (err) => setEditError(err.response?.data?.error || 'Cập nhật thất bại'),
    }
  );

  const deleteMutation = useMutation(accountsAPI.delete, {
    onSuccess: () => queryClient.invalidateQueries('accounts'),
  });

  const createUserMutation = useMutation(usersAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      setUserDialogOpen(false);
      setUserFormData({ username: '', password: '', role: 'viewer', allowed_accounts: [], allowed_tabs: [], permissions: emptyPermissionsForm() });
      setUserError('');
    },
    onError: (err) => setUserError(err.response?.data?.error || 'Tạo user thất bại'),
  });

  const deleteUserMutation = useMutation(usersAPI.delete, {
    onSuccess: () => queryClient.invalidateQueries('users'),
  });

  const updateUserMutation = useMutation(
    ({ id, data }) => usersAPI.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries('users'); setUserEditDialogOpen(false); setEditingUser(null); setUserEditError(''); },
      onError: (err) => setUserEditError(err.response?.data?.error || 'Cập nhật thất bại'),
    }
  );

  const handleEditUserOpen = (u) => {
    setEditingUser(u);
    setUserEditFormData({
      password: '',
      allowed_accounts: u.allowed_accounts || [],
      allowed_tabs: u.allowed_tabs || [],
      permissions: permissionsToForm(u.permissions),
    });
    setUserEditError('');
    setUserEditDialogOpen(true);
  };

  const handleEditUserSubmit = (e) => {
    e.preventDefault();
    const { permissions, ...rest } = userEditFormData;
    const data = { ...rest, permissions: buildPermissionsPayload(permissions) };
    if (!data.password) delete data.password;
    updateUserMutation.mutate({ id: editingUser.id, data });
  };

  const handleCreateUserSubmit = (e) => {
    e.preventDefault();
    const { permissions, ...rest } = userFormData;
    const payload = { ...rest, permissions: buildPermissionsPayload(permissions) };
    createUserMutation.mutate(payload);
  };

  const resetForm = () => { setFormData({ mt5_account_id: '', name: '', password: '', group_name: '', profit_share: '' }); setError(''); };

  const handleEditOpen = (account) => {
    setEditingAccount(account);
    // hidden_locations stored as JSON array string (or null) on the server.
    let hidden = [];
    try {
      const parsed = typeof account.hidden_locations === 'string'
        ? JSON.parse(account.hidden_locations)
        : account.hidden_locations;
      if (Array.isArray(parsed)) hidden = parsed;
    } catch { /* malformed → treat as visible */ }
    setEditFormData({ mt5_account_id: account.mt5_account_id || '', name: account.name || '', password: '', group_name: account.group_name || '', profit_share: account.profit_share != null ? String(account.profit_share) : '', hidden_locations: hidden, is_paused: Number(account.is_paused) === 1 });
    setEditError('');
    setEditDialogOpen(true);
  };

  // Toggle a single hide-location key in the edit form.
  const toggleHiddenLocation = (key) => {
    setEditFormData((prev) => {
      const set = new Set(prev.hidden_locations || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...prev, hidden_locations: [...set] };
    });
  };

  // Master switch: ON hides everywhere (default), OFF makes fully visible.
  const toggleHideAccount = (on) => {
    setEditFormData((prev) => ({
      ...prev,
      hidden_locations: on ? ['dashboard', 'master_performance'] : [],
    }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const data = { ...editFormData };
    if (!data.password) delete data.password;
    updateMutation.mutate({ id: editingAccount.id, data });
  };

  const handleDeleteUser = (id) => {
    if (id === currentUser?.id) return;
    if (window.confirm('Bạn có chắc muốn xóa user này?')) deleteUserMutation.mutate(id);
  };

  // Trạng thái tài khoản (cột "Trạng thái", 2026-08-24):
  //   1. Admin đánh dấu is_paused=1            → "Tạm ngưng" (xám)
  //   2. Không đánh dấu → theo EA thực tế (AccountStatus): có cập nhật ≤ 5 phút
  //      & is_running=1 → "Hoạt động" (xanh); is_running=0 → "Dừng EA" (cam);
  //      không có status / cập nhật > 5 phút → "Offline" (xám nhạt).
  const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
  const accountStatusInfo = (account) => {
    if (Number(account.is_paused) === 1) {
      return { key: 'paused', label: 'Tạm ngưng', color: '#9ca3af', bg: '#9ca3af22', tip: 'Admin đánh dấu tạm ngưng (sửa tài khoản để bật lại)' };
    }
    const ts = account.ea_last_updated ? new Date(account.ea_last_updated.replace(' ', 'T') + 'Z') : null;
    const ageMs = ts && !isNaN(ts) ? Date.now() - ts.getTime() : null;
    const lastStr = ts && !isNaN(ts) ? ts.toLocaleString('vi-VN') : 'chưa có';
    if (ageMs == null || ageMs > ACTIVE_WINDOW_MS) {
      return { key: 'offline', label: 'Offline', color: '#6b7280', bg: '#6b728022', tip: `EA không gửi trạng thái > 5 phút (cập nhật cuối: ${lastStr})` };
    }
    if (Number(account.ea_is_running) === 1) {
      return { key: 'running', label: 'Hoạt động', color: '#4ade80', bg: '#22c55e22', tip: `EA đang chạy (cập nhật ${lastStr})` };
    }
    return { key: 'stopped', label: 'Dừng EA', color: '#fbbf24', bg: '#f59e0b22', tip: `EA online nhưng Algo đang tắt (cập nhật ${lastStr})` };
  };

  if (!isAdmin()) {
    return <Box><Alert severity="info">Bạn không có quyền truy cập trang này.</Alert></Box>;
  }

  // Renders the detailed-permissions block. Shared between the create + edit
  // viewer dialogs. `formData` must have a `permissions` key shaped by
  // emptyPermissionsForm()/permissionsToForm.
  const renderPermsBlock = (formData, setFormData) => {
    const perm = formData.permissions;
    const updatePerm = (patch) => setFormData({ ...formData, permissions: { ...perm, ...patch } });
    return (
      <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Phân quyền chi tiết
        </Typography>

        {/* Dashboard columns */}
        <FormControlLabel
          control={
            <Switch
              checked={perm.restrictColumns}
              onChange={(e) => updatePerm({ restrictColumns: e.target.checked })}
            />
          }
          label="Hạn chế cột Dashboard"
        />
        {perm.restrictColumns && (
          <Box sx={{ ml: 4, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Chọn cột viewer được xem (cột Tài khoản luôn hiển thị):
            </Typography>
            <FormGroup row>
              {DASHBOARD_COLUMNS.map(col => (
                <FormControlLabel
                  key={col.key}
                  disabled={col.alwaysOn}
                  control={
                    <Checkbox
                      size="small"
                      checked={col.alwaysOn || perm.columns.includes(col.key)}
                      onChange={(e) => {
                        if (col.alwaysOn) return;
                        const cols = e.target.checked
                          ? [...perm.columns, col.key]
                          : perm.columns.filter(k => k !== col.key);
                        updatePerm({ columns: cols });
                      }}
                    />
                  }
                  label={col.label}
                  sx={{ minWidth: 170, m: 0 }}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* Setup popup access */}
        <FormControlLabel
          control={
            <Switch
              checked={perm.viewSetup}
              onChange={(e) => updatePerm({ viewSetup: e.target.checked })}
            />
          }
          label="Cho phép xem Setup tài khoản"
          sx={{ display: 'block' }}
        />
        {perm.viewSetup && (
          <Box sx={{ ml: 4, mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={perm.customSetupAccounts}
                  onChange={(e) => updatePerm({ customSetupAccounts: e.target.checked })}
                />
              }
              label="Đặt riêng danh sách tài khoản (mặc định = giống danh sách được xem)"
            />
            {perm.customSetupAccounts && (
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Tài khoản được xem Setup</InputLabel>
                <Select
                  multiple
                  value={perm.setupAccounts}
                  onChange={(e) => updatePerm({ setupAccounts: e.target.value })}
                  input={<OutlinedInput label="Tài khoản được xem Setup" />}
                  renderValue={(selected) => selected.length === 0 ? '(chưa chọn — sẽ chặn tất cả)' : selected.join(', ')}
                >
                  {accounts.map((a) => (
                    <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                      <Checkbox checked={perm.setupAccounts.includes(a.mt5_account_id)} />
                      <ListItemText primary={`${a.mt5_account_id} — ${a.name}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Reports sub-tabs */}
        <FormControlLabel
          control={
            <Switch
              checked={perm.restrictReports}
              onChange={(e) => updatePerm({ restrictReports: e.target.checked })}
            />
          }
          label="Hạn chế tab Báo cáo"
          sx={{ display: 'block' }}
        />
        {perm.restrictReports && (
          <Box sx={{ ml: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Chọn báo cáo viewer được xem:
            </Typography>
            <FormGroup row>
              {REPORTS_SUB_TABS.map(t => (
                <FormControlLabel
                  key={t.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={perm.reports.includes(t.key)}
                      onChange={(e) => {
                        const reports = e.target.checked
                          ? [...perm.reports, t.key]
                          : perm.reports.filter(k => k !== t.key);
                        updatePerm({ reports });
                      }}
                    />
                  }
                  label={t.label}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* Master Performance sub-tabs */}
        <FormControlLabel
          control={
            <Switch
              checked={perm.restrictMasterPerf}
              onChange={(e) => updatePerm({ restrictMasterPerf: e.target.checked })}
            />
          }
          label="Hạn chế tab Hiệu suất Master"
          sx={{ display: 'block' }}
        />
        {perm.restrictMasterPerf && (
          <Box sx={{ ml: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Chọn sub-tab viewer được xem (Tổng quan / Đánh giá cảm xúc):
            </Typography>
            <FormGroup row>
              {MASTER_PERF_SUB_TABS.map(t => (
                <FormControlLabel
                  key={t.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={perm.masterPerf.includes(t.key)}
                      onChange={(e) => {
                        const masterPerf = e.target.checked
                          ? [...perm.masterPerf, t.key]
                          : perm.masterPerf.filter(k => k !== t.key);
                        updatePerm({ masterPerf });
                      }}
                    />
                  }
                  label={t.label}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* Per-account overrides */}
        {renderAccountOverridesBlock(formData, setFormData)}
      </Box>
    );
  };

  // Per-account override section. Lets admin pick accounts (from
  // formData.allowed_accounts; empty = all accounts) and customize columns /
  // sub-tabs just for those accounts. Override entries only contribute to the
  // payload when their internal switches are turned on.
  const renderAccountOverridesBlock = (formData, setFormData) => {
    const perm = formData.permissions;
    const overrides = perm.overrides || {};
    const overrideKeys = Object.keys(overrides);

    // Account list to choose from: viewer's allowed_accounts (if set) else all.
    const candidates = (formData.allowed_accounts && formData.allowed_accounts.length > 0)
      ? formData.allowed_accounts
      : accounts.map(a => a.mt5_account_id);
    const accountLabel = (id) => {
      const acc = accounts.find(a => String(a.mt5_account_id) === String(id));
      return acc ? `${id} — ${acc.name}` : String(id);
    };

    const updatePerm = (patch) => setFormData({ ...formData, permissions: { ...perm, ...patch } });
    const updateOverride = (accId, patch) => updatePerm({
      overrides: { ...overrides, [accId]: { ...(overrides[accId] || emptyOverride()), ...patch } }
    });
    const removeOverride = (accId) => {
      const next = { ...overrides };
      delete next[accId];
      updatePerm({ overrides: next });
    };
    const availableForAdd = candidates.filter(id => !overrides[String(id)]);

    return (
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed rgba(255,255,255,0.15)' }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
          Tùy chỉnh riêng cho tài khoản
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Account không có override sẽ dùng quy tắc chung phía trên. Chỉ các tài khoản viewer được xem mới chọn được.
        </Typography>

        {availableForAdd.length > 0 && (
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>+ Thêm account để tùy chỉnh</InputLabel>
            <Select
              value=""
              input={<OutlinedInput label="+ Thêm account để tùy chỉnh" />}
              onChange={(e) => {
                const id = String(e.target.value);
                if (id) updateOverride(id, {});
              }}
            >
              {availableForAdd.map(id => (
                <MenuItem key={id} value={id}>{accountLabel(id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {overrideKeys.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            (Chưa có override nào)
          </Typography>
        ) : overrideKeys.map(accId => {
          const ov = overrides[accId];
          return (
            <Box key={accId} sx={{ mt: 1, p: 1.5, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{accountLabel(accId)}</Typography>
                <Button size="small" color="error" onClick={() => removeOverride(accId)}>Bỏ override</Button>
              </Box>

              {/* Column override */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={ov.restrictColumns}
                    onChange={(e) => updateOverride(accId, { restrictColumns: e.target.checked })}
                  />
                }
                label="Hạn chế cột Dashboard riêng cho account này"
                sx={{ display: 'block' }}
              />
              {ov.restrictColumns && (
                <Box sx={{ ml: 4, mb: 1 }}>
                  <FormGroup row>
                    {DASHBOARD_COLUMNS.map(col => (
                      <FormControlLabel
                        key={col.key}
                        disabled={col.alwaysOn}
                        control={
                          <Checkbox
                            size="small"
                            checked={col.alwaysOn || ov.columns.includes(col.key)}
                            onChange={(e) => {
                              if (col.alwaysOn) return;
                              const cols = e.target.checked
                                ? [...ov.columns, col.key]
                                : ov.columns.filter(k => k !== col.key);
                              updateOverride(accId, { columns: cols });
                            }}
                          />
                        }
                        label={col.label}
                        sx={{ minWidth: 170, m: 0 }}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              {/* Reports override */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={ov.restrictReports}
                    onChange={(e) => updateOverride(accId, { restrictReports: e.target.checked })}
                  />
                }
                label="Hạn chế báo cáo riêng cho account này"
                sx={{ display: 'block' }}
              />
              {ov.restrictReports && (
                <Box sx={{ ml: 4 }}>
                  <FormGroup row>
                    {REPORTS_SUB_TABS.map(t => (
                      <FormControlLabel
                        key={t.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={ov.reports.includes(t.key)}
                            onChange={(e) => {
                              const reports = e.target.checked
                                ? [...ov.reports, t.key]
                                : ov.reports.filter(k => k !== t.key);
                              updateOverride(accId, { reports });
                            }}
                          />
                        }
                        label={t.label}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              {/* Master Performance sub-tab override */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={ov.restrictMasterPerf}
                    onChange={(e) => updateOverride(accId, { restrictMasterPerf: e.target.checked })}
                  />
                }
                label="Hạn chế tab Hiệu suất Master riêng cho account này"
                sx={{ display: 'block' }}
              />
              {ov.restrictMasterPerf && (
                <Box sx={{ ml: 4 }}>
                  <FormGroup row>
                    {MASTER_PERF_SUB_TABS.map(t => (
                      <FormControlLabel
                        key={t.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={ov.masterPerf.includes(t.key)}
                            onChange={(e) => {
                              const masterPerf = e.target.checked
                                ? [...ov.masterPerf, t.key]
                                : ov.masterPerf.filter(k => k !== t.key);
                              updateOverride(accId, { masterPerf });
                            }}
                          />
                        }
                        label={t.label}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* ── Account Management ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Quản lý tài khoản</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Tạo tài khoản
        </Button>
      </Box>

      <Box mb={2}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Lọc theo Group</InputLabel>
          <Select value={groupFilter} label="Lọc theo Group" onChange={(e) => setGroupFilter(e.target.value)}>
            <MenuItem value="">-- Tất cả --</MenuItem>
            {[...new Set(accounts.map(a => a.group_name).filter(Boolean))].sort().map(g => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID MT5</TableCell>
                <TableCell>Tên</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.filter(a => !groupFilter || a.group_name === groupFilter).length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">Chưa có tài khoản nào</TableCell></TableRow>
              ) : (
                accounts.filter(a => !groupFilter || a.group_name === groupFilter).map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.mt5_account_id}</TableCell>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>{account.username}</TableCell>
                    <TableCell>{account.group_name || '-'}</TableCell>
                    <TableCell>
                      {(() => {
                        const st = accountStatusInfo(account);
                        return (
                          <Tooltip title={st.tip}>
                            <Chip label={st.label} size="small"
                              sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, color: st.color, bgcolor: st.bg, border: `1px solid ${st.color}55` }} />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{new Date(account.created_at).toLocaleDateString('vi-VN')}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleEditOpen(account)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => deleteMutation.mutate(account.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* ── User Management ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Quản lý người dùng</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUserDialogOpen(true)}>
          Tạo User
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center">Chưa có user nào</TableCell></TableRow>
              ) : (
                users.map((u) => {
                  const roleInfo = ROLE_LABELS[u.role] || { label: u.role, color: 'default' };
                  return (
                    <TableRow key={u.id}>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>
                        <Chip label={roleInfo.label} color={roleInfo.color} size="small" />
                      </TableCell>
                      <TableCell>{new Date(u.created_at).toLocaleDateString('vi-VN')}</TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleEditUserOpen(u)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          disabled={u.id === currentUser?.id}
                          onClick={() => handleDeleteUser(u.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog tạo tài khoản */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }}>
          <DialogTitle>Tạo tài khoản mới</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField margin="normal" required fullWidth label="MT5 Account ID" value={formData.mt5_account_id} onChange={(e) => setFormData({ ...formData, mt5_account_id: e.target.value })} />
            <TextField margin="normal" required fullWidth label="Tên khách hàng" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            <TextField margin="normal" required fullWidth type="password" label="Mật khẩu" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} helperText="Tối thiểu 6 ký tự" />
            <TextField margin="normal" fullWidth label="Group" value={formData.group_name} onChange={(e) => setFormData({ ...formData, group_name: e.target.value })} helperText="Tùy chọn — nhóm phân loại tài khoản" />
            <TextField margin="normal" fullWidth label="Tỷ lệ chia LN cho Master (%)" type="number" inputProps={{ min: 0, max: 100, step: 1 }} value={formData.profit_share} onChange={(e) => setFormData({ ...formData, profit_share: e.target.value })} helperText="% lợi nhuận chia cho Master (InpMasterProfitShare) — 0 nếu không chia" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Hủy</Button>
            <Button type="submit" variant="contained">Tạo</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog chỉnh sửa tài khoản */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleEditSubmit}>
          <DialogTitle>Chỉnh sửa tài khoản</DialogTitle>
          <DialogContent>
            {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
            <TextField margin="normal" required fullWidth label="MT5 Account ID" value={editFormData.mt5_account_id} onChange={(e) => setEditFormData({ ...editFormData, mt5_account_id: e.target.value })} />
            <TextField margin="normal" required fullWidth label="Tên khách hàng" value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} />
            <TextField margin="normal" fullWidth type="password" label="Mật khẩu mới" value={editFormData.password} onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })} helperText="Để trống nếu không muốn đổi mật khẩu" />
            <TextField margin="normal" fullWidth label="Group" value={editFormData.group_name} onChange={(e) => setEditFormData({ ...editFormData, group_name: e.target.value })} helperText="Tùy chọn — nhóm phân loại tài khoản" />
            <TextField margin="normal" fullWidth label="Tỷ lệ chia LN cho Master (%)" type="number" inputProps={{ min: 0, max: 100, step: 1 }} value={editFormData.profit_share} onChange={(e) => setEditFormData({ ...editFormData, profit_share: e.target.value })} helperText="% lợi nhuận chia cho Master (InpMasterProfitShare) — 0 nếu không chia" />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Trạng thái tài khoản</Typography>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  color="warning"
                  checked={!!editFormData.is_paused}
                  onChange={(e) => setEditFormData({ ...editFormData, is_paused: e.target.checked })}
                />
              }
              label="Tạm ngưng tài khoản"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Bật = cột Trạng thái hiện "Tạm ngưng" (nhãn quản lý, không gửi lệnh tới EA). Tắt = tự hiện theo EA thực tế: Hoạt động / Dừng EA / Offline.
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Ẩn / Hiện tài khoản</Typography>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  checked={(editFormData.hidden_locations || []).length > 0}
                  onChange={(e) => toggleHideAccount(e.target.checked)}
                />
              }
              label="Ẩn tài khoản này"
            />
            {(editFormData.hidden_locations || []).length > 0 && (
              <Box sx={{ pl: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Chọn nơi ẩn:
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(editFormData.hidden_locations || []).includes('dashboard')}
                        onChange={() => toggleHiddenLocation('dashboard')}
                      />
                    }
                    label="Dashboard"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(editFormData.hidden_locations || []).includes('master_performance')}
                        onChange={() => toggleHiddenLocation('master_performance')}
                      />
                    }
                    label="Hiệu suất Master"
                  />
                </FormGroup>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Hủy</Button>
            <Button type="submit" variant="contained" disabled={updateMutation.isLoading}>Lưu</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog tạo User */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleCreateUserSubmit}>
          <DialogTitle>Tạo User mới</DialogTitle>
          <DialogContent>
            {userError && <Alert severity="error" sx={{ mb: 2 }}>{userError}</Alert>}
            <TextField margin="normal" required fullWidth label="Username" value={userFormData.username} onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })} />
            <TextField margin="normal" required fullWidth type="password" label="Mật khẩu" value={userFormData.password} onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })} helperText="Tối thiểu 6 ký tự" />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select value={userFormData.role} label="Role" onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value, allowed_accounts: [], allowed_tabs: [], permissions: emptyPermissionsForm() })}>
                <MenuItem value="viewer">Viewer — Chỉ xem</MenuItem>
                <MenuItem value="client">Client — Xem tài khoản của mình</MenuItem>
                <MenuItem value="admin-sub">Admin Sub</MenuItem>
                {currentUser?.role === 'admin-root' && <MenuItem value="admin-root">Admin Root</MenuItem>}
              </Select>
            </FormControl>

            {userFormData.role === 'viewer' && (
              <Box sx={{ mt: 1 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Tài khoản được xem</InputLabel>
                  <Select
                    multiple
                    value={userFormData.allowed_accounts}
                    onChange={(e) => setUserFormData({ ...userFormData, allowed_accounts: e.target.value })}
                    input={<OutlinedInput label="Tài khoản được xem" />}
                    renderValue={(selected) => selected.length === 0 ? 'Tất cả tài khoản' : selected.join(', ')}
                  >
                    {accounts.map((a) => (
                      <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                        <Checkbox checked={userFormData.allowed_accounts.includes(a.mt5_account_id)} />
                        <ListItemText primary={`${a.mt5_account_id} — ${a.name}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
                  Tab được phép xem:
                </Typography>
                <FormGroup row>
                  {[{ key: 'dashboard', label: 'Dashboard' }, { key: 'reports', label: 'Báo cáo' }, { key: 'master-performance', label: 'Hiệu suất Master' }].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={userFormData.allowed_tabs.includes(key)}
                          onChange={(e) => {
                            const tabs = e.target.checked
                              ? [...userFormData.allowed_tabs, key]
                              : userFormData.allowed_tabs.filter(t => t !== key);
                            setUserFormData({ ...userFormData, allowed_tabs: tabs });
                          }}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="text.secondary">
                  Để trống = cho phép xem tất cả tab
                </Typography>

                {renderPermsBlock(userFormData, setUserFormData)}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserDialogOpen(false)}>Hủy</Button>
            <Button type="submit" variant="contained" disabled={createUserMutation.isLoading}>Tạo</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog chỉnh sửa User */}
      <Dialog open={userEditDialogOpen} onClose={() => setUserEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleEditUserSubmit}>
          <DialogTitle>Chỉnh sửa User: {editingUser?.username}</DialogTitle>
          <DialogContent>
            {userEditError && <Alert severity="error" sx={{ mb: 2 }}>{userEditError}</Alert>}
            <TextField
              margin="normal" fullWidth type="password" label="Mật khẩu mới"
              value={userEditFormData.password}
              onChange={(e) => setUserEditFormData({ ...userEditFormData, password: e.target.value })}
              helperText="Để trống nếu không muốn đổi mật khẩu"
            />
            {editingUser?.role === 'viewer' && (
              <Box sx={{ mt: 1 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Tài khoản được xem</InputLabel>
                  <Select
                    multiple
                    value={userEditFormData.allowed_accounts}
                    onChange={(e) => setUserEditFormData({ ...userEditFormData, allowed_accounts: e.target.value })}
                    input={<OutlinedInput label="Tài khoản được xem" />}
                    renderValue={(selected) => selected.length === 0 ? 'Tất cả tài khoản' : selected.join(', ')}
                  >
                    {accounts.map((a) => (
                      <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                        <Checkbox checked={userEditFormData.allowed_accounts.includes(a.mt5_account_id)} />
                        <ListItemText primary={`${a.mt5_account_id} — ${a.name}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
                  Tab được phép xem:
                </Typography>
                <FormGroup row>
                  {[{ key: 'dashboard', label: 'Dashboard' }, { key: 'reports', label: 'Báo cáo' }, { key: 'master-performance', label: 'Hiệu suất Master' }].map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={userEditFormData.allowed_tabs.includes(key)}
                          onChange={(e) => {
                            const tabs = e.target.checked
                              ? [...userEditFormData.allowed_tabs, key]
                              : userEditFormData.allowed_tabs.filter(t => t !== key);
                            setUserEditFormData({ ...userEditFormData, allowed_tabs: tabs });
                          }}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="text.secondary">
                  Để trống = cho phép xem tất cả tab
                </Typography>

                {renderPermsBlock(userEditFormData, setUserEditFormData)}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserEditDialogOpen(false)}>Hủy</Button>
            <Button type="submit" variant="contained" disabled={updateUserMutation.isLoading}>Lưu</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
